import { useCallback } from 'react';

import getNights from '@shared/utils/getNights';
import useQueuedCallback from '@shared/v2/hooks/useQueuedCallback';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';
import {
  FlightJourney,
  ReturnFlightJourney,
} from '@shared/v2/services/types/api/parsedResponse';

import { useCustomerSelection } from '@/components/CustomerSelectionProvider';
import useTiers from '@/hooks/useTiers';
import { convertCashToPoints } from '@/lib/pointsPay';
import dataLayer from '@/v2/utils/dataLayer/dataLayer';
import {
  getEcommerceFlightItem,
  hasReturnFlightJourney,
} from '@/v2/utils/dataLayer/eventDataHelpers';
import { getFlightOriginDestination } from '@/v2/utils/getFlightOriginDestination';

export interface UseFlightOptionInteractionsEventProps {
  availablePackageCount?: number;
  flightDirection?: 'Departing' | 'Returning';
}

export interface FlightOptionInteractionsEventData {
  flightOption: FlightJourney | ReturnFlightJourney;
  packagePrice: number;
  action?: 'select' | 'add';
}

const useFlightOptionInteractionsEvent = ({
  availablePackageCount,
  flightDirection = 'Departing',
}: UseFlightOptionInteractionsEventProps) => {
  const { searchQuery, isReady } = useSearchQuery();
  const { paymentType } = useCustomerSelection();
  const tiers = useTiers();

  const handleInvokeFlightOptionInteractionsEvent = useCallback(
    (eventData: FlightOptionInteractionsEventData) => {
      if (!isReady || !searchQuery) return;

      const duration =
        searchQuery.departureDate && searchQuery.returnDate
          ? getNights(searchQuery)
          : undefined;

      const pointsValue =
        tiers && eventData.packagePrice
          ? convertCashToPoints(eventData.packagePrice, tiers).toNumber()
          : undefined;

      const travellers = {
        adults: searchQuery.adults || 2,
        children: searchQuery.children || 0,
        infants: searchQuery.infants || 0,
      };

      const flightType =
        flightDirection === 'Departing' ? 'Departure' : 'Return';

      const hasReturnFlight = hasReturnFlightJourney(eventData.flightOption);

      const item = getEcommerceFlightItem({
        travellers,
        packagePrice: eventData.packagePrice,
        flightOption: eventData.flightOption,
        flightType: hasReturnFlight ? 'Departure / Return' : flightType,
        index: 1,
      });

      const { origin, destination } = getFlightOriginDestination({
        flightOption: eventData.flightOption,
        flightDirection,
      });

      const travelDetails = {
        origin,
        destination,
        duration,
        departureDate: searchQuery.departureDate,
        returnDate: searchQuery.returnDate,
      };

      return dataLayer.flightOptionInteractionsEvent({
        item: { ...item, points_value: pointsValue },
        paymentType,
        availablePackageCount,
        priceChange: eventData.flightOption.charges.total.amount,
        pointsValue,
        travelDetails,
        action: eventData.action,
      });
    },
    [
      isReady,
      availablePackageCount,
      flightDirection,
      searchQuery,
      paymentType,
      tiers,
    ],
  );

  const [queuedHandleFlightOptionInteractionEvent] = useQueuedCallback(
    handleInvokeFlightOptionInteractionsEvent,
    {
      isCallbackReady: isReady,
      autoRunQueueWhenReady: true,
    },
  );

  return {
    isReady,
    handleInvokeFlightOptionInteractionsEvent:
      queuedHandleFlightOptionInteractionEvent as (
        eventData: FlightOptionInteractionsEventData,
      ) => void,
  };
};

export default useFlightOptionInteractionsEvent;
